import React from "react";
import { Building2, Activity, CheckCircle2, Clock, Users, ShieldCheck, AlertTriangle, PieChart } from "lucide-react";
import OpsTopBar from "../../components/ops/OpsTopBar";
import useApi from "../../hooks/useApi";

function Bars({ items, colorKey }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  const palette = ["#2F6BFF", "#6FA015", "#B8892B", "#D0526B", "#7A5AF8", "#0EA5A5", "#E0691A"];
  return (
    <div className="space-y-2.5">
      {items.map((i, idx) => (
        <div key={i.name || i.day} className="flex items-center gap-3">
          <span className="w-24 text-xs text-ink-2 truncate">{i.name || i.day}</span>
          <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
            <div className="h-full rounded-full" style={{ width: `${(i.value / max) * 100}%`, background: colorKey ? palette[idx % palette.length] : "var(--grad-green)" }} />
          </div>
          <span className="w-14 text-right text-xs font-bold text-ink tabular-nums">{i.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

export default function OpsManagement() {
  const { data } = useApi("/ops/management");
  if (!data) return (<><OpsTopBar title="Performance" /><div className="p-8 text-ink-2">Loading…</div></>);
  const s = data.stats;

  const cards = [
    { l: "Managed branches", v: s.managed_branches, icon: Building2 },
    { l: "Actions this month", v: s.actions_this_month.toLocaleString(), icon: Activity },
    { l: "Completed today", v: s.completed_today, icon: CheckCircle2 },
    { l: "Remaining today", v: s.remaining_today, icon: Clock },
    { l: "Operators active", v: s.operators_active, icon: Users },
    { l: "Avg task time", v: s.avg_task_time, icon: Clock },
    { l: "SLA", v: `${s.sla}%`, icon: ShieldCheck },
    { l: "Error rate", v: `${s.error_rate}%`, icon: AlertTriangle },
  ];

  return (
    <>
      <OpsTopBar title="Performance" subtitle="Managed services · management view" />
      <div className="p-6 sm:p-8 max-w-[1300px] space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.l} className="card p-5" data-testid={`mgmt-${c.l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <c.icon size={16} color="#5F8213" strokeWidth={1.75} />
              <div className="text-3xl font-extrabold tracking-tighter text-ink mt-2">{c.v}</div>
              <div className="text-xs text-ink-2 mt-0.5">{c.l}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="card p-5"><span className="label-caps">Actions per day</span><div className="mt-4"><Bars items={s.actions_per_day} /></div></div>
          <div className="card p-5"><span className="label-caps">By platform</span><div className="mt-4"><Bars items={s.by_platform} colorKey /></div></div>
          <div className="card p-5"><span className="label-caps">By operator</span><div className="mt-4"><Bars items={s.by_operator} colorKey /></div></div>
        </div>

        {/* Commercials */}
        <div className="card overflow-hidden" data-testid="mgmt-commercials">
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "#E7E8E4" }}>
            <PieChart size={15} color="#5F8213" /><span className="label-caps">Commercials · allowance utilisation</span>
          </div>
          <div className="hidden sm:grid grid-cols-12 px-5 py-2.5 label-caps border-b" style={{ borderColor: "#E7E8E4" }}>
            <span className="col-span-5">Branch</span><span className="col-span-2">Price</span><span className="col-span-2">Used</span><span className="col-span-3">Utilisation</span>
          </div>
          <div className="divide-y" style={{ borderColor: "#E7E8E4" }}>
            {data.commercials.map((c) => {
              const color = c.utilisation >= 100 ? "#D0526B" : c.utilisation >= 80 ? "#B8892B" : "#93C020";
              return (
                <div key={c.branch} className="grid grid-cols-12 items-center px-5 py-3 gap-2 text-sm">
                  <span className="col-span-6 sm:col-span-5 font-medium text-ink truncate">{c.branch}</span>
                  <span className="col-span-6 sm:col-span-2 text-ink-2">£{c.price}/mo</span>
                  <span className="col-span-6 sm:col-span-2 text-ink-2">{c.used}/{c.allowance}</span>
                  <span className="col-span-6 sm:col-span-3 flex items-center gap-2">
                    <span className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}><span className="block h-full rounded-full" style={{ width: `${Math.min(100, c.utilisation)}%`, background: color }} /></span>
                    <span className="w-9 text-right font-bold" style={{ color }}>{c.utilisation}%</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
